import { CombinedGraphQLErrors } from '@base-app/web2'

// Client side of the rich-text rule. The SERVER is the source of truth (the sanitizer in
// admin-mobifull-api/src/rich-text); this is its mirror so the editor never holds, emits or
// pastes anything the server would strip. Change the two together.
//
// Same allowlist: p br strong em a[href] ul ol li hr; b -> strong, i -> em, div -> p; every
// attribute dropped except a safe href; script/style/iframe/object/embed/noscript/template/
// textarea/title/xmp/svg/math dropped with their content; unknown tags lose the tag and keep
// their text; comments are ignored. The walk runs over a copy parsed by DOMParser (an inert
// document: no script runs, no image loads) and rebuilds the output into a detached element. The
// untrusted markup is never put into the live document.

// Mirrors MAX_RICH_TEXT_LENGTH in the API
export const MAX_RICH_TEXT_LENGTH = 100_000
// Mirrors MAX_DEPTH in the API
const MAX_DEPTH = 20

const DROP_WITH_CONTENT = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'noscript',
  'template',
  'textarea',
  'title',
  'xmp',
  'svg',
  'math',
])
const RENAMED: Record<string, string> = { b: 'strong', i: 'em', div: 'p' }
const KEPT = new Set(['p', 'strong', 'em', 'ul', 'ol', 'li'])
const ALLOWED_SCHEME = /^(?:https?|tel|mailto):/i

// The href to keep, or null. Same rule as the server: ASCII control characters removed, the ends
// trimmed, a space inside becomes %20, and only http, https, tel and mailto links with something
// after the scheme survive.
export function cleanRichTextHref(raw: string): string | null {
  let stripped = ''
  for (const ch of raw) {
    const code = ch.codePointAt(0) as number
    if (code > 0x1f && code !== 0x7f && !isBidiControl(code)) {
      stripped += ch
    }
  }
  const href = stripped.trim().split(' ').join('%20')
  const scheme = ALLOWED_SCHEME.exec(href)
  return scheme && href.length > scheme[0].length ? href : null
}

// Bidi embedding, override and isolate controls (U+202A-202E, U+2066-2069): removed, as on the
// server (the API's rich-text/bidi.ts). ZWJ/ZWNJ, LRM/RLM and emoji sequences are kept.
const isBidiControl = (code: number) =>
  (code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069)

function stripBidiControls(value: string): string {
  let out = ''
  for (const ch of value) {
    if (!isBidiControl(ch.codePointAt(0) as number)) {
      out += ch
    }
  }
  return out
}

const isList = (el: Element) => el.localName === 'ul' || el.localName === 'ol'

// An element with nothing in it (or only whitespace) is removed, like the server does
function hasContent(el: Element): boolean {
  return (el.textContent ?? '').trim() !== '' || el.querySelector('br, hr') !== null
}

function walk(source: Node, target: Element, doc: Document, depth: number, inLink: boolean) {
  // Text and inline content directly inside a list goes into an <li>
  let implicitItem: Element | null = null
  const into = (isItemContent: boolean): Element => {
    if (!isList(target) || !isItemContent) {
      return target
    }
    if (!implicitItem) {
      implicitItem = doc.createElement('li')
      target.appendChild(implicitItem)
    }
    return implicitItem
  }

  for (const node of Array.from(source.childNodes)) {
    if (node.nodeType === 3) {
      const text = stripBidiControls(node.textContent ?? '')
      if (isList(target) && text.trim() === '') {
        continue
      }
      into(true).appendChild(doc.createTextNode(text))
      continue
    }
    if (node.nodeType !== 1) {
      // comments, processing instructions
      continue
    }
    const el = node as Element
    const raw = el.localName.toLowerCase()
    if (DROP_WITH_CONTENT.has(raw)) {
      continue
    }
    const name = RENAMED[raw] ?? raw
    if (name === 'br' || name === 'hr') {
      into(true).appendChild(doc.createElement(name))
      continue
    }
    if (name === 'a') {
      const href = inLink ? null : cleanRichTextHref(el.getAttribute('href') ?? '')
      if (href !== null && depth < MAX_DEPTH) {
        const link = doc.createElement('a')
        link.setAttribute('href', href)
        walk(el, link, doc, depth + 1, true)
        if (hasContent(link)) {
          into(true).appendChild(link)
        }
      } else {
        // No valid link: the text stays
        walk(el, into(true), doc, depth, inLink)
      }
      continue
    }
    if (!KEPT.has(name) || depth >= MAX_DEPTH || (name === 'li' && !isList(target))) {
      walk(el, into(true), doc, depth, inLink)
      continue
    }
    if (name === 'li') {
      implicitItem = null
    }
    const copy = doc.createElement(name)
    walk(el, copy, doc, depth + 1, inLink)
    if (hasContent(copy)) {
      into(name !== 'li').appendChild(copy)
    }
  }
}

function walkOnce(html: string): { html: string; empty: boolean } {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const out = doc.createElement('div')
  walk(doc.body, out, doc, 0, false)
  return {
    html: out.innerHTML.trim(),
    // From the output tree itself: no second parse to find out whether anything is visible
    empty: (out.textContent ?? '').trim() === '' && out.querySelector('hr') === null,
  }
}

// The sanitized HTML for untrusted markup (a paste, a stored value) and whether nothing visible is
// left. Never throws. The walk runs until the output stops changing: re-parsing our own output lets
// the browser's parser fix the structure the walk cannot see from the inside (<div><div> becoming
// <p><p> closes the first p), which is the job the server's tree builder does. Idempotent by
// construction. The last pass is the one that decides `empty`, so a keystroke parses only here.
export function analyzeRichText(html: string): { html: string; empty: boolean } {
  let current = html
  let result = { html, empty: false }
  for (let pass = 0; pass < 4; pass++) {
    result = walkOnce(current)
    if (result.html === current) {
      break
    }
    current = result.html
  }
  return { html: current, empty: result.empty }
}

export function sanitizeRichTextHtml(html: string): string {
  return analyzeRichText(html).html
}

// True when there is no text and no rule: the editor emits '' then, not "<p><br></p>"
export function isRichTextEmpty(html: string): boolean {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return (doc.body.textContent ?? '').trim() === '' && doc.body.querySelector('hr') === null
}

// Escapes everything that matters in text and in a double-quoted attribute: & < > ". Used to put a
// URL into inserted html, where a reference such as &copy; must stay literal text.
export const escapeHtml = (text: string): string =>
  text
    .split('&')
    .join('&amp;')
    .split('<')
    .join('&lt;')
    .split('>')
    .join('&gt;')
    .split('"')
    .join('&quot;')

// A well-formed character reference (&ccedil; &#231;) passes through, as on the server: legacy
// text keeps its accents. A bare & < > is escaped.
const REFERENCE = /&(?:#[0-9]+|#[xX][0-9a-fA-F]+|[A-Za-z][A-Za-z0-9]*);/y

function escapeTextKeepingReferences(text: string): string {
  let out = ''
  let i = 0
  while (i < text.length) {
    const ch = text[i]
    if (ch === '&') {
      REFERENCE.lastIndex = i
      const ref = REFERENCE.exec(text)
      out += ref ? ref[0] : '&amp;'
      i += ref ? ref[0].length : 1
      continue
    }
    out += ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch
    i++
  }
  return out
}

// Old plain-text values (no tags): escaped, a blank line starts a paragraph, a line break is <br>
export function plainTextToHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .filter(paragraph => paragraph.trim() !== '')
    .map(
      paragraph =>
        `<p>${escapeTextKeepingReferences(paragraph.trim()).split('\n').join('<br>')}</p>`,
    )
    .join('')
}

// A stored value has tags when something that looks like a tag, comment or declaration is in it
const HAS_TAGS = /<[a-zA-Z!/?]/

// What the editor shows for a stored value: old plain text becomes paragraphs, anything with
// tags goes through the allowlist walk
export function richTextToEditorHtml(value: string): string {
  return HAS_TAGS.test(value) ? sanitizeRichTextHtml(value) : plainTextToHtml(value)
}

// The first `extensions.reason` of a GraphQL error is this one
const hasReason = (err: Error, reason: string) =>
  CombinedGraphQLErrors.is(err) &&
  err.errors.some(gqlError => gqlError.extensions?.reason === reason)

// Is this the API refusing a rich text above MAX_RICH_TEXT_LENGTH?
export const isRichTextTooLongError = (err: Error): boolean => hasReason(err, 'RICH_TEXT_TOO_LONG')

// Is this the API refusing a subtitle above MAX_SUBTITLE_LENGTH?
export const isSubtitleTooLongError = (err: Error): boolean => hasReason(err, 'SUBTITLE_TOO_LONG')
export const isSortOrderInvalidError = (err: Error): boolean => hasReason(err, 'SORT_ORDER_INVALID')
export const isPastorOrderInvalidError = (err: Error): boolean =>
  hasReason(err, 'PASTOR_ORDER_INVALID')
export const isPastorRankInvalidError = (err: Error): boolean =>
  hasReason(err, 'PASTOR_RANK_INVALID')

// After an error is set on a field, put focus there once the error has rendered: a screen reader
// then reads the field and its error instead of staying silent on the Save button. The editor's
// contenteditable and the inputs carry the field's id.
export function focusFieldById(id: string): void {
  setTimeout(() => document.getElementById(id)?.focus(), 0)
}
